import { useEffect, useMemo, useState } from "react";
import locationsService from "services/locations-service";
import { validateLocationKey } from "../helperFunction";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDropzone from "components/MDDropzone";
import MDTypography from "components/MDTypography";
import Grid from "@mui/material/Grid";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from "@mui/material/CircularProgress";
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import {
    getUploadingSx,
    getUploadedSx,
    getNotUploadedSx,
    getIdleSx,
    getImageContainerSx,
    getDropzoneSx,
    getImageSx,
    getUploadButtonSx
} from './styles';

import ImageModal from "e4acomponents/ImageModal";

export default function LocationImage({
    errors,
    newLocation,
    setNewLocation,
    editMode = false,
    setErrors,
    id = undefined
}) {
    // Holds the image shown in current image section
    const [imageUrl, setImageUrl] = useState("");

    // Stores the new image until upload
    const [newImage, setNewImage] = useState("");

    // Upload loading states
    const [isUploading, setIsUploading] = useState(false);
    const [isUploaded, setIsUploaded] = useState(false);
    const [isNotUploaded, setIsNotUploaded] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    // hardcoded fix for upload button size
    const [buttonSize, setButtonSize] = useState('150px');

    // If in edit mode, fetch and display existing image
    useEffect(()=>{
        if(editMode && newLocation.imagePath){
            const fetchImage = async () => {
                try {
                    const encodedPath = encodeURIComponent(newLocation.imagePath);
                    const imageData = await locationsService.getLocationImage(encodedPath);
                    setImageUrl(imageData);
                } catch (error) {
                    console.error("Error fetching image:", error);
                }
            };
            fetchImage();
        }
    },[editMode, newLocation.imagePath]);

    const handleOpenImageModal = () => {
        if(imageUrl){
            setIsImageModalOpen(true);
        }
    };

    const handleCloseImageModal = () => {
        setIsImageModalOpen(false);
    };

    // Whenever an image is added to the dropzone, it is stored in newImage
    const onImageAdd = (file) => {
        setNewImage(file);
        setButtonSize('100%');
        if (!editMode) {
            setNewLocation(prev => ({ ...prev, imageData: file }));
        }
    };

    const uploadSucess = () => {
        setIsUploaded(true);
        setTimeout(() => {
            setIsUploaded(false);
        }, 1000);
    };

    const uploadFail = () => {
        setIsNotUploaded(true);
        setTimeout(() => {
            setIsNotUploaded(false);
        }, 1000);
    }

    const validate = () => {
        const [ok, errs] = validateLocationKey(newLocation);
        setErrors(errs);
        return ok;
    };

    // Deletes previous partcode's image then replace it with the new image
    const changeImage = async () => {
        try {
            if (!newImage) {
                return;
            }
            if (!validate()) {
                return;
            }
            setIsUploading(true);

            const payload = {
                data: {
                    attributes: {
                        filename: newImage.upload.filename,
                        dataUrl: newImage.dataURL,
                        folderPath: newLocation.locationKey,
                        city: newLocation.city,
                        previousKey: newLocation.imagePath ? newLocation.imagePath : undefined,
                        id: id
                    }
                }
            }
            const response = await locationsService.uploadLocationImage(payload);
            setIsUploading(false);

            if (response.s3Key) {
                setImageUrl(newImage.dataURL);
                setNewLocation(prev => ({ ...prev, imagePath: response.s3Key }));
                uploadSucess();
            } else {
                uploadFail();
            }
        } catch (error) {
            console.error("Error uploading image: ", error);
            setIsUploading(false);
            uploadFail();
        }
    };

    const uploadingSx = getUploadingSx(isUploading);
    const uploadedSx = getUploadedSx(isUploaded);
    const notUploadedSx = getNotUploadedSx(isNotUploaded);
    const idleSx = getIdleSx(isUploading, isUploaded, isNotUploaded);
    const imageContainerSx = getImageContainerSx(isImageLoaded);
    const imageSx = getImageSx();
    const dropzoneSx = getDropzoneSx(isImageLoaded);
    const uploadButtonSx = getUploadButtonSx(buttonSize);

    return (
        <>  
            <ImageModal
                mediaURL={imageUrl}
                open={isImageModalOpen}
                onClose={handleCloseImageModal}
            />
            <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                    Location Image
                </MDTypography>
            </MDBox>

            <MDBox>
                <MDTypography variant="h6" fontWeight="light" mt={1} mb={2}>
                    Upload an image to represent the location.
                </MDTypography>

                <Grid container spacing={1}>

                    <Grid item xs={12} md={editMode ? 10 : 12}>
                        <MDBox display="flex">
                            {imageUrl && (
                                <MDBox sx={imageContainerSx} >
                                    <img
                                        src={imageUrl}
                                        alt="Part Code"
                                        onLoad={() => setIsImageLoaded(true)}
                                        onError={() => setIsImageLoaded(false)}
                                        onClick={handleOpenImageModal}
                                        style={imageSx}
                                    />
                                </MDBox>
                            )
                            }
                            <MDBox width="100%" sx={dropzoneSx}>
                                {useMemo(
                                    () => (
                                        <MDDropzone
                                            options={{ addRemoveLinks: true, maxFiles: 1, acceptedFiles: 'image/*' }}
                                            acceptedFiles="image/*"
                                            onFileAdd={onImageAdd}
                                        />
                                    ),
                                    []
                                )}
                            </MDBox>
                        </MDBox>
                    </Grid>

                    {editMode && (
                        <Grid item xs={12} md={2}>
                            <MDButton
                                p={2}
                                fullWidth
                                variant="contained"
                                color="primary"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={uploadButtonSx} 
                                onClick={changeImage}
                            >
                                {/* Layered icons with transitions for smooth crossfades */}
                                <MDBox sx={uploadingSx}>
                                    <CircularProgress size={24} color="white" />
                                </MDBox>

                                <MDBox sx={uploadedSx}>
                                    <DoneAllIcon color="white" sx={{ transform: 'scale(1.8)' }} />
                                </MDBox>

                                <MDBox sx={notUploadedSx}>
                                    <CloseIcon color="white" sx={{ transform: 'scale(1.8)' }} />
                                </MDBox>

                                <MDBox sx={idleSx}>
                                    <CloudUploadIcon color="white" sx={{ transform: 'scale(1.8)' }} />
                                </MDBox>

                            </MDButton>
                        </Grid>
                    )}
                </Grid>

                {errors.imagePath && (
                    <MDTypography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errors.imagePath}
                    </MDTypography>
                )}

            </MDBox>
        </>
    )
}